import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGoBack } from "@/lib/useGoBack";
import { MapPin, DollarSign, BookmarkPlus, BookmarkCheck, BadgeCheck, Globe, ExternalLink, Users } from "lucide-react";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";
import MapLink from "@/components/MapLink";
import CreatorTag from "@/components/CreatorTag";
import ClaimedByTag from "@/components/ClaimedByTag";
import ContactsDisplay from "@/components/ContactsDisplay";
import EditHistory from "@/components/EditHistory";
import WikiEditModal from "@/components/WikiEditModal";
import MessageBoard from "@/components/MessageBoard";
import EntityEvents from "@/components/EntityEvents";
import { ManageModeratorsModal } from "@/components/ClaimedListingsPanel";
import Reviews from "@/components/Reviews";
import FlagButton from "@/components/FlagButton";
import DistanceZip from "@/components/DistanceZip";
import { savedNearZip, saveFilters, getSavedFilters } from "@/lib/filterCookies";
import { Button } from "@/components/ui/button";
import useFitOneLine from "@/lib/useFitOneLine";
import HeroLogoBadge from "@/components/HeroLogoBadge";
import { usePhotoLightbox, HeroCarousel } from "@/components/PhotoGallery";
import FollowAlertsBell from "@/components/FollowAlertsBell";
import ConfirmDialog from "@/components/ConfirmDialog";
import Seo from "@/components/Seo";

export default function TrainerListingDetail() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const autoEdit = searchParams.get("edit") === "1";
    const goBack = useGoBack();
    const { user, isFollowing, toggleFollow } = useAuth();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [claiming, setClaiming] = useState(false);
    const [followBusy, setFollowBusy] = useState(false);
    const [eventsBump, setEventsBump] = useState(0);
    const [showMods, setShowMods] = useState(false);
    const [showRelinquish, setShowRelinquish] = useState(false);
    const [zipState, setZipState] = useState(savedNearZip());
    const [coords, setCoords] = useState(null);  // precise device lat/lng, when granted
    const { open: openPhoto, node: photoLightbox } = usePhotoLightbox((listing && listing.photos) || [], (listing && listing.name) || "");
    const handleZip = (z, c) => {
        setZipState(z);
        setCoords(c || null);
        saveFilters({ ...getSavedFilters({}), near_zip: z });
    };
    const nameFitRef = useFitOneLine(listing?.name || "", { max: 48, reserve: listing?.owner_verified ? 40 : 0 });

    useEffect(() => {
        const params = {};
        if (coords) { params.near_lat = coords.lat; params.near_lng = coords.lng; if (zipState) params.near_zip = zipState; }
        else if (zipState) { params.near_zip = zipState; }
        api.get(`/trainer-listings/${id}`, { params })
            .then((r) => setListing(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [id, zipState, coords]);

    if (loading) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;
    if (notFound || !listing) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="trainer-listing-notfound">
                <div className="font-display text-4xl">Trainer not found</div>
                <button onClick={goBack} data-testid="not-found-back" className="text-[#2196f3] hover:underline text-sm mt-4 inline-block">← Back</button>
            </div>
        );
    }

    const isVerified = !!listing.owner_verified;
    // Owner-only management controls only show when acting as the trainer (or admin) —
    // not when the same account is browsing under a fan profile.
    const isManaging = !!(user && (user.is_admin || (listing.owner_user_id === user.id && user.active_profile === "trainer")));

    const onClaim = async () => {
        if (!user) return toast.error("Please log in to claim this trainer");
        if (!user.is_pro) return toast.error("Only Pro members can claim listings — upgrade to Pro on your Account page");
        if (!listing.poc_name) return toast.error("This listing has no point of contact yet, so it can't be claimed. A point of contact must be added — and it must match your account name — before anyone can claim it.");
        setClaiming(true);
        try {
            await api.post(`/claims/trainer_listing/${listing.id}`);
            const fresh = await api.get(`/trainer-listings/${listing.id}`);
            setListing(fresh.data);
            toast.success("Claimed — your name and contact matched this listing");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not claim listing");
        } finally {
            setClaiming(false);
        }
    };

    const onRelinquish = async () => {
        try {
            await api.post(`/trainer-listings/${listing.id}/relinquish`);
            const fresh = await api.get(`/trainer-listings/${listing.id}`);
            setListing(fresh.data);
            toast.success("You've relinquished ownership");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not relinquish");
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-6 md:px-8 py-10" data-testid="trainer-listing-detail-page">
            <Seo
                title={`${listing.name}${listing.sport ? ` — ${listing.sport} Trainer` : " — Trainer"}${listing.city && listing.state ? ` (${listing.city}, ${listing.state})` : ""}`}
                description={`${listing.name} offers ${listing.sport || "sports"} training${listing.city && listing.state ? ` in ${listing.city}, ${listing.state}` : ""}. View services, location and contact info on TravelSports.info.`}
                canonical={`/trainer-listings/${listing.id}`}
                image={(listing.photos && listing.photos[0]) || undefined}
                jsonLd={{ "@context": "https://schema.org", "@type": "SportsActivityLocation", name: listing.name, ...(listing.city && listing.state ? { address: { "@type": "PostalAddress", addressLocality: listing.city, addressRegion: listing.state, addressCountry: "US" } } : {}) }}
            />
            <ConfirmDialog
                open={showRelinquish}
                onOpenChange={setShowRelinquish}
                title="Relinquish ownership"
                description="Relinquish ownership of this listing? It will become unclaimed and anyone can claim it again."
                confirmLabel="Relinquish"
                destructive
                onConfirm={onRelinquish}
                testid="relinquish-confirm-dialog"
            />
            {Array.isArray(listing.photos) && listing.photos.length > 0 ? (
                <div className="mb-6" data-testid="trainer-listing-detail-photo-wrap">
                    <HeroCarousel
                        photos={listing.photos}
                        name={listing.name}
                        onOpen={openPhoto}
                        testid="trainer-hero-carousel"
                        badge={(listing.avatar || listing.logo || listing.photo) ? (
                            <HeroLogoBadge src={listing.avatar || listing.logo || listing.photo} alt={`${listing.name} logo`} bg={listing.logo_bg} testid="trainer-listing-detail-logo-badge" />
                        ) : null}
                    />
                </div>
            ) : listing.detail_photo && (
                <div className="relative mb-6 aspect-[16/9] overflow-hidden bg-[#0a0a0a]">
                    <img src={listing.detail_photo} alt={listing.name} data-testid="trainer-listing-detail-photo" className="block w-full h-full object-cover" />
                    {(listing.avatar || listing.logo || listing.photo) && (
                        <HeroLogoBadge src={listing.avatar || listing.logo || listing.photo} alt={`${listing.name} logo`} bg={listing.logo_bg} testid="trainer-listing-detail-logo-badge" />
                    )}
                </div>
            )}

            <div className="flex flex-col md:flex-row items-start gap-6">
                {!listing.detail_photo && (listing.avatar || listing.logo || listing.photo) && (
                    <img
                        src={listing.avatar || listing.logo || listing.photo}
                        alt={listing.name}
                        data-testid="trainer-listing-photo"
                        className="h-28 w-28 rounded-full border border-[#2a2a2a] object-cover shrink-0"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-[#2196f3] text-xs uppercase tracking-[0.3em] mb-1 flex items-center gap-2">
                        Trainer{listing.sport ? ` · ${listing.sport}` : ""}
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                        <h1 ref={nameFitRef} data-testid="trainer-listing-name" className="font-display tracking-wide leading-none whitespace-nowrap overflow-hidden min-w-0">{listing.name}</h1>
                        {isVerified && <BadgeCheck className="h-6 w-6 text-[#22c55e] shrink-0" data-testid="trainer-verified-check" />}
                    </div>

                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <EditHistory entity={listing} entityType="trainer_listing" entityId={listing.id} onReverted={(fresh) => setListing(fresh)} />
                        <WikiEditModal type="trainerListing" entity={listing} autoOpen={autoEdit} onSaved={() => window.location.reload()} />
                        <FlagButton targetType="trainer_listing" targetId={listing.id} label="Report listing" />
                        {isVerified && (user?.is_admin || listing.owner_user_id === user?.id) && (
                            <button type="button" data-testid="relinquish-btn" onClick={() => setShowRelinquish(true)}
                                className="text-[11px] uppercase tracking-wider text-[#a0a0a0] hover:text-[#ef4444] border border-[#555] hover:border-[#ef4444] px-3 py-1.5 transition-colors">
                                Relinquish Ownership
                            </button>
                        )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#a0a0a0]">
                        {(() => {
                            const realStreet = listing.street_address && listing.street_address !== "Not listed" ? listing.street_address : "";
                            const hasLoc = realStreet || listing.city || listing.state || listing.home_zip;
                            if (!hasLoc) return null;
                            const parts = [realStreet, listing.city, listing.state, listing.home_zip];
                            return (
                                <>
                                    <MapLink direct parts={parts} testid="trainer-listing-location" className="inline-flex items-center gap-1.5 hover:text-[#2196f3] transition-colors">
                                        <MapPin className="h-4 w-4 text-[#2196f3]" />
                                        {realStreet ? `${realStreet}, ` : ""}{[listing.city, listing.state].filter(Boolean).join(", ")} {listing.home_zip}
                                    </MapLink>
                                </>
                            );
                        })()}
                        {listing.hourly_rate != null && (
                            <span className="inline-flex items-center gap-1.5"><DollarSign className="h-4 w-4 text-[#2196f3]" />${listing.hourly_rate}/hr</span>
                        )}
                        <DistanceZip distanceMiles={listing.distance_miles} zip={zipState} onZip={handleZip} accent="#2196f3" testid="trainer-listing-distance" />
                    </div>
                </div>
                <div className="w-full md:w-72 shrink-0 flex flex-col gap-3 items-stretch">
                    {/* 1 + 2: Share and Follow compressed onto the same row */}
                    <div className="flex items-stretch gap-3">
                    <ShareButton
                        accent="#2196f3"
                        className={`${user ? (isFollowing("trainer_listing", listing.id) ? "flex-[0.55]" : "flex-1") : "w-full"} h-12 text-xs`}
                        testid="share-trainer-listing-btn"
                        shareUrl={`${window.location.origin}/trainer-listings/${listing.id}`}
                        title={`${listing.name} · ${listing.sport || "Sports"} Trainer`}
                        description={`${[listing.city, listing.state].filter(Boolean).join(", ")}`}
                    />
                    {/* Follow */}
                    {user && (
                        <div className={`${isFollowing("trainer_listing", listing.id) ? "flex-[1.45]" : "flex-1"} flex items-stretch min-w-0`}>
                        <button
                            type="button"
                            data-testid="follow-listing-btn"
                            title={isFollowing("trainer_listing", listing.id) ? "Following" : "Follow"}
                            aria-label={isFollowing("trainer_listing", listing.id) ? "Following" : "Follow"}
                            disabled={followBusy}
                            onClick={async () => {
                                setFollowBusy(true);
                                const res = await toggleFollow("trainer_listing", listing.id);
                                setFollowBusy(false);
                                if (res.ok) toast.success(res.following ? "You'll be notified when this listing changes" : "Unfollowed");
                                else toast.error(res.error || "Follow failed");
                            }}
                            className={`${isFollowing("trainer_listing", listing.id) ? "flex-1 min-w-0 overflow-hidden" : "w-full"} rounded-none uppercase tracking-wider whitespace-nowrap inline-flex items-center justify-center gap-1.5 h-12 px-4 text-xs font-semibold transition-colors ${isFollowing("trainer_listing", listing.id) ? "bg-[#2196f3] text-white hover:bg-[#1976d2]" : "bg-[#141414] border border-[#2196f3] text-[#2196f3] hover:bg-[#2196f3]/10"}`}
                        >
                            {isFollowing("trainer_listing", listing.id)
                                ? <BookmarkCheck className="h-4 w-4" />
                                : <BookmarkPlus className="h-4 w-4" />} {isFollowing("trainer_listing", listing.id) ? "Following" : "Follow"}
                        </button>
                        {isFollowing("trainer_listing", listing.id) && (
                            <FollowAlertsBell targetType="trainer_listing" targetId={listing.id} size={16} triggerStyle={{ color: "#fff" }} triggerClassName="inline-flex items-center justify-center h-12 w-12 shrink-0 bg-[#2196f3] border-l border-white/30 hover:bg-[#1976d2] transition-colors" />
                        )}
                        </div>
                    )}
                    </div>
                    {/* 3. Claimed by / Added by card */}
                    {isVerified ? (
                        <ClaimedByTag userId={listing.owner_user_id} contextType="trainer_listing" contextId={listing.id} reportType="trainer_listing" verified />
                    ) : (listing.created_by && (
                        <CreatorTag block userId={listing.created_by} label="Posted by" />
                    ))}
                    {/* Owner / management actions */}
                    {user && !isVerified && (
                        <Button
                            data-testid="claim-trainer-listing-btn"
                            disabled={claiming}
                            onClick={onClaim}
                            className="w-full rounded-none uppercase tracking-wider text-xs bg-[#141414] border border-[#2196f3] text-[#2196f3] hover:bg-[#2196f3] hover:text-white h-10 disabled:opacity-40 disabled:hover:bg-[#141414] disabled:hover:text-[#2196f3]"
                        >
                            <BadgeCheck className="h-4 w-4 mr-2" /> {claiming ? "Claiming…" : "Claim"}
                        </Button>
                    )}
                    {isManaging && (
                        <Button
                            data-testid="add-moderators-btn"
                            onClick={() => setShowMods(true)}
                            className="w-full rounded-none uppercase tracking-wider text-xs bg-[#141414] border border-[#2196f3] text-[#2196f3] hover:bg-[#2196f3] hover:text-white h-10"
                        >
                            <Users className="h-4 w-4 mr-2" /> Add moderators
                        </Button>
                    )}
                </div>
            </div>

            <div className="h-px bg-[#2a2a2a] my-8" />

            {listing.bio && (
                <section className="mt-2">
                    <h2 className="font-display text-2xl tracking-wide mb-3">About</h2>
                    <p data-testid="trainer-listing-bio" className="text-sm text-[#cfcfcf] leading-relaxed whitespace-pre-line">{listing.bio}</p>
                </section>
            )}

            {photoLightbox}

            <EntityEvents params={{ entity_owner: listing.owner_user_id || undefined, entity_kind: "trainer", trainer: listing.id }} manageTrainerId={(isManaging || user?.is_admin) ? listing.id : ""} greenHeading refreshToken={eventsBump} addEventLink={user && listing.owner_user_id === user.id && isVerified ? `/add/${encodeURIComponent(listing.sport || "Basketball")}?type=event&claim_type=trainer_listing&claim_id=${listing.id}` : ""} />

            <EntityEvents
                params={{ mention: listing.name, exclude_owner: listing.owner_user_id || listing.created_by, exclude_kind: "trainer" }}
                title={`Events mentioning ${listing.name}`}
                manageTrainerId={(isManaging || user?.is_admin) ? listing.id : ""}
                canClaim={!!(user && (user.is_admin || listing.owner_user_id === user.id))}
                refreshToken={eventsBump}
                onChanged={() => setEventsBump((x) => x + 1)}
            />

            {((listing.contacts || []).length > 0 || listing.poc_name) && (
                <section className="mt-8" data-testid="trainer-contact-section">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Contact / Social Media</div>
                    {listing.poc_name && (
                        <div className="text-lg font-semibold text-white mb-3" data-testid="trainer-listing-poc">{listing.poc_name}</div>
                    )}
                    {(listing.contacts || []).length > 0 && (
                        <ContactsDisplay contacts={listing.contacts} accent="#2196f3" testidPrefix="trainer-contact-disp" hideTitle />
                    )}
                </section>
            )}


            {listing.website && (
                <section className="mt-8">
                    <a href={listing.website} target="_blank" rel="noopener noreferrer" data-testid="trainer-listing-website" className="inline-flex items-center gap-3 border border-[#2a2a2a] p-4 hover:border-[#2196f3] transition-colors">
                        <Globe className="h-4 w-4 text-[#2196f3]" />
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">Website</div>
                            <div className="truncate text-sm">{listing.website}</div>
                        </div>
                        <ExternalLink className="h-3.5 w-3.5 text-[#a0a0a0]" />
                    </a>
                </section>
            )}

            <MessageBoard entityType="trainer_listing" entityId={listing.id} accent="#2196f3" />
            <Reviews entityType="trainer_listing" entityId={listing.id} canRespond={isManaging} accent="#2196f3" />
            {showMods && <ManageModeratorsModal entityType="trainer_listing" entityId={listing.id} name={listing.name} accent="#2196f3" onClose={() => setShowMods(false)} />}
        </div>
    );
}
